import {
  Box,
  Button,
  Card,
  CardProps,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  Flex,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tag,
  useDisclosure,
} from '@televet/kibble-ui';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import {
  AutomationRunFormCompleted,
  AutomationRunFormSent,
  AutomationRunPaymentCompleted,
  AutomationRunPaymentSent,
} from 'assets/icons';
import { env } from 'env';
import he from 'he';
import AutomationRunStatement from 'pages/Conversations/components/ChannelMessageOld/MessageBubble/Attachments/AutomationRun/prompts/AutomationRunStatement';
import AutomationRunSystemMessage from 'pages/Conversations/components/ChannelMessageOld/MessageBubble/Attachments/AutomationRun/prompts/AutomationRunSystemMessage';
import DevModeDisplayJSON from 'pages/Settings/components/DevModeDisplayJSON';
import useTaskVisualizer from 'pages/Tasks/hooks/useTaskVisualizer';
import React, { useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  AutomationRunActionFragment,
  AutomationRunActionPromptType,
  AutomationRunActionType,
  AutomationRunStatus,
  TaskStatus,
  useGetAutomationRunQuery,
  useResetAutomationRunMutation,
  useSubscribeToAutomationRunActionSubscription,
  WorkflowEvent,
} from 'shared/types/graphql';
import NewTaskNotificationDot from './NewTaskNotificationDot';
import AutomationRunButtonsPrompt from './prompts/AutomationRunButtonsPrompt';
import AutomationRunFormPrompt from './prompts/AutomationRunFormPrompt';
import AutomationRunGeneralPrompt from './prompts/AutomationRunGeneralPrompt';
import AutomationRunPaymentPrompt from './prompts/AutomationRunPaymentPrompt';

type IAutomationRunProps = {
  automationRunId: string;
  setCurrentInvoiceId: (invoiceId: string) => void;
  isLastMessage: boolean;
  isOneBeforeLastMessage: boolean;
  isLastMessageSystemMessage: boolean;
  hideBorder?: boolean;
  hideCollapse?: boolean;
  useChannelMessageStyles?: boolean;
};

const isProduction = env.REACT_APP_PRODUCTION === 'true';

const AutomationRun = ({
  automationRunId,
  setCurrentInvoiceId,
  isLastMessage,
  isLastMessageSystemMessage,
  isOneBeforeLastMessage,
  hideBorder = false,
  hideCollapse = false,
  useChannelMessageStyles,
}: IAutomationRunProps): JSX.Element => {
  const { trackEvent } = useTracking();

  const { data: automationRunData, refetch } = useGetAutomationRunQuery({
    variables: { where: { id: automationRunId } },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  useSubscribeToAutomationRunActionSubscription({
    variables: { automationRunId: automationRunId },
    skip: !automationRunId,
    onData: () => {
      refetch();
    },

    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const automationRunActions = automationRunData?.findUniqueAutomationRun?.actions;

  const automationRun = automationRunData?.findUniqueAutomationRun;

  const automationRunTask = automationRunData?.findUniqueAutomationRun?.tasks?.[0];

  const { renderTaskDetails, renderTaskStatusSelector, getTask } = useTaskVisualizer({
    taskId: automationRunTask?.id,
    canRevertComplete: true,
  });

  const lastAutomatedActionIndex = useMemo(() => {
    for (let index = (automationRunActions?.length || 1) - 1; index >= 0; index--) {
      if (automationRunActions?.[index]?.actionType === AutomationRunActionType.Automated) {
        return index;
      }
    }
    return 0;
  }, [automationRunActions]);

  const renderAction = (
    automationRunAction: AutomationRunActionFragment | undefined,
    actionIndex: number,
  ): JSX.Element | null => {
    if (automationRunAction?.body) {
      automationRunAction.body = he.decode(automationRunAction?.body);
    }
    if (automationRunAction?.actionType === AutomationRunActionType.System)
      return <AutomationRunSystemMessage automationRunAction={automationRunAction} />;

    const promptType = automationRunAction?.promptType;

    switch (promptType) {
      case AutomationRunActionPromptType.Prompt:
        return <AutomationRunGeneralPrompt automationRunAction={automationRunAction} actionIndex={actionIndex} />;

      case AutomationRunActionPromptType.Payment:
        return (
          <AutomationRunPaymentPrompt
            automationRunAction={automationRunAction}
            setCurrentInvoiceId={setCurrentInvoiceId}
            actionIndex={actionIndex}
          />
        );
      case AutomationRunActionPromptType.Form:
        return <AutomationRunFormPrompt automationRunAction={automationRunAction} actionIndex={actionIndex} />;
      case AutomationRunActionPromptType.Buttons:
        return (
          <AutomationRunButtonsPrompt
            automationRunAction={automationRunAction}
            actionIndex={actionIndex}
            lastAutomatedActionIndex={lastAutomatedActionIndex}
            isSurveyAutomation={isSurvey}
          />
        );
      case AutomationRunActionPromptType.Statement:
        return <AutomationRunStatement automationRunAction={automationRunAction} actionIndex={actionIndex} />;
    }

    return null;
  };

  const automationRunStatus = useMemo(() => {
    switch (automationRun?.status) {
      case AutomationRunStatus.Sent:
        return <Tag variant="default" label="Not Started" />;
      case AutomationRunStatus.Started:
        return <Tag variant="warning" label="In Progress" />;
      case AutomationRunStatus.Completed:
        return <Tag variant="success" label="Complete" />;
    }
    return <></>;
  }, [automationRun?.status]);

  enum AutomationRunIcons {
    FormSent = 'FormSent',
    FormCompleted = 'FormCompleted',
    PaymentSent = 'PaymentSent',
    PaymentCompleted = 'PaymentCompleted',
  }

  const automationRunIcons = useMemo(() => {
    const iconsArray = [];
    if (automationRunActions) {
      for (const automationRunAction of automationRunActions) {
        switch (automationRunAction.promptType) {
          case AutomationRunActionPromptType.Form:
            automationRunAction.formSubmission?.submittedAt
              ? iconsArray.push(AutomationRunIcons.FormCompleted)
              : iconsArray.push(AutomationRunIcons.FormSent);
            break;
          case AutomationRunActionPromptType.Payment:
            automationRunAction?.paymentIntent?.paidAt
              ? iconsArray.push(AutomationRunIcons.PaymentCompleted)
              : iconsArray.push(AutomationRunIcons.PaymentSent);
            break;
        }
      }
    }
    return iconsArray;
  }, [AutomationRunIcons, automationRunActions]);

  const renderAutomationRunIcon = (automationRunIcon: AutomationRunIcons): JSX.Element => {
    switch (automationRunIcon) {
      case AutomationRunIcons.FormSent:
        return <AutomationRunFormSent style={{ width: 25, height: 25 }} />;
      case AutomationRunIcons.FormCompleted:
        return <AutomationRunFormCompleted style={{ width: 25, height: 25 }} />;
      case AutomationRunIcons.PaymentSent:
        return <AutomationRunPaymentSent style={{ width: 25, height: 25 }} />;
      case AutomationRunIcons.PaymentCompleted:
        return <AutomationRunPaymentCompleted style={{ width: 25, height: 25 }} />;
    }
  };

  const isSurvey = useMemo(
    () => (automationRunActions || []).some((action) => !!action.automationAction?.workflowEventSetting?.surveyStep),
    [automationRunActions],
  );

  const isPaymentRequest = useMemo(
    () => (automationRunActions || []).some((action) => action.promptType === AutomationRunActionPromptType.Payment),
    [automationRunActions],
  );

  // these are global - shared across all clinics
  // allow reset of dev day 21 for testing
  const hillsToHomeWorkflowIds = useMemo(
    () => [
      'clt4maxgw00041a014y57rgd8', // dev day0
      'cltd7jpds006yxl01h31qqcsn', // prod day0
      'clyrhn97a0jq0x80147eii66y', // prod day10
      'cltd7mq3q00gyyt01lwbsun01', // prod day21
    ],
    [],
  );

  const isHillsRegistrationWorkflow = useMemo(
    () =>
      (automationRunActions || []).some(
        (action) =>
          action.automationAction?.workflowEventSetting?.event === WorkflowEvent.InvoiceLineItemCreated &&
          hillsToHomeWorkflowIds.includes(action.automationAction.workflowEventSetting.id),
      ),
    [automationRunActions, hillsToHomeWorkflowIds],
  );

  // can reset if it's not a survey, payment request, or prod Hill's to Home workflow
  const canReset = useMemo(
    () => !isSurvey && !isPaymentRequest && !(isHillsRegistrationWorkflow && isProduction),
    [isHillsRegistrationWorkflow, isPaymentRequest, isSurvey],
  );

  const resetMessage = useMemo(() => {
    let reason = 'contains a payment request';

    if (isSurvey) {
      reason = 'is a survey';
    }

    if (isHillsRegistrationWorkflow) {
      reason = "is a Hill's to Home registration";
    }

    return `This automation cannot be reset because it ${reason}.`;
  }, [isHillsRegistrationWorkflow, isSurvey]);

  const [isResetLoading, setIsResetLoading] = useState(false);

  const [resetAutomationRun] = useResetAutomationRunMutation();

  const handleReset = async (): Promise<void> => {
    setIsResetLoading(true);
    if (canReset) {
      await resetAutomationRun({
        variables: { where: { id: automationRunId } },
      });
      trackEvent(ConversationEvents.AutomationRunReset);
      await refetch();
      onClose();
    }
    setIsResetLoading(false);
  };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCollapsed, setIsCollapsed] = useState(
    // We want to render the automation expanded if it is the last message in the conversation
    // or if the message is one before the last message and that last message its a system message
    !(isLastMessage || (isOneBeforeLastMessage && isLastMessageSystemMessage)),
  );

  const toggleCollapse = (): void => {
    setIsCollapsed((isCollapsed) => {
      if (isCollapsed) {
        trackEvent(ConversationEvents.AutomationRunExpand);
      } else {
        trackEvent(ConversationEvents.AutomationRunCollapse);
      }
      return !isCollapsed;
    });
  };

  return (
    <Card
      pt={6}
      pb={3}
      px="0"
      {...(hideBorder && { borderColor: 'transparent' })}
      {...(useChannelMessageStyles &&
        ({
          flex: 1,
          maxWidth: 600,
          minHeight: 100,
        } as CardProps))}
    >
      <Box cursor="pointer" onClick={toggleCollapse} className="AutomationRun__Title">
        <Flex justifyContent="space-between" alignItems="center" px={3} mb={2} className="Header">
          <>
            {automationRunStatus}
            <Flex flex={1} className="AutomationRun__Icons">
              {automationRunIcons.map((icon, index) => {
                return (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    mr={2}
                    key={`automation-run-${automationRun?.id}-icon-${index}`}
                    className="IconWrapper"
                  >
                    {renderAutomationRunIcon(icon)}
                  </Flex>
                );
              })}
              {!!automationRunTask && (
                <Flex gap={1} alignItems="center" ml={2}>
                  <Flex position="relative">
                    <Icon name="taskList" size="xl" />
                    {automationRunTask.status !== TaskStatus.Completed && <NewTaskNotificationDot />}
                  </Flex>
                  <Text size="sm" fontWeight="bold">
                    Task {automationRunTask.status === TaskStatus.Completed ? 'Completed' : 'Created'}
                  </Text>
                </Flex>
              )}
            </Flex>
          </>
          {automationRunActions?.length !== 1 && !isCollapsed && (
            <Button
              size="xs"
              variant="ghostNeutral"
              aria-label="Reset"
              iconName="arrowCircle"
              onClick={(e): void => {
                e.stopPropagation();
                onOpen();
              }}
            >
              Reset
            </Button>
          )}
          {!hideCollapse && (
            <Button
              size="xs"
              variant="ghostNeutral"
              onClick={(e): void => {
                e.stopPropagation();
                toggleCollapse();
              }}
              iconName={!isCollapsed ? 'arrowsOut' : 'arrowsIn'}
              _focus={{ border: 'none' }}
              aria-label="Toggle Automation Run"
              ml={2}
            />
          )}
        </Flex>
        <Text
          fontWeight="bold"
          size="md"
          marginBottom={isCollapsed ? 0 : 1}
          px={3}
          className="AutomationRun__AutomationName"
        >
          {automationRun?.actions?.[0]?.automationAction?.workflowEventSetting?.name || ''}
        </Text>
      </Box>
      {!isCollapsed && (
        <>
          {automationRunActions?.map((automationRunAction, actionIndex) => {
            return <div key={automationRunAction.id}>{renderAction(automationRunAction, actionIndex)}</div>;
          })}
          {!!automationRunTask && !!getTask() && (
            <Flex
              justifyContent="space-between"
              backgroundColor="background.secondaryAlpha"
              borderLeftWidth="4px"
              borderLeftColor="border.info"
              mt={1}
              mx={3}
              pr={2}
              pl={4}
              py={5}
            >
              {renderTaskDetails()}
              {renderTaskStatusSelector()}
            </Flex>
          )}
        </>
      )}
      <CModal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <CModalHeader>Reset Automation</CModalHeader>
          <ModalCloseButton _focus={{ border: 'none' }} />
          <CModalBody>
            {canReset ? (
              <>
                Resetting this automation will remove all messages within the automation that were sent and submitted
                including any inputs and forms.
                <br />
                <br />
                This will NOT change the conversation status, delete data exported to a practice management software, or
                refund paid invoices.
                <br />
                <br />
                <span style={{ fontWeight: 'bold' }}>Are you sure you want to reset the automation?</span>
              </>
            ) : (
              <>{resetMessage}</>
            )}
          </CModalBody>

          <CModalFooter>
            {canReset ? (
              <Flex className="AutomationRun__ModalButtons" flex={1} justifyContent="space-between">
                <Button variant="tertiary" onClick={onClose}>
                  Cancel
                </Button>
                {canReset && (
                  <Button variant="primaryDestructive" onClick={handleReset} isLoading={isResetLoading}>
                    Reset Automation
                  </Button>
                )}
              </Flex>
            ) : (
              <Box className="AutomationRun__ModalButton" flex={1} justifyContent="flex-end">
                <Button onClick={onClose} isLoading={isResetLoading}>
                  Got It
                </Button>
              </Box>
            )}
          </CModalFooter>
        </ModalContent>
      </CModal>
      <DevModeDisplayJSON src={{ automationRun }} />
    </Card>
  );
};

export default AutomationRun;
